import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMatchWorkspace } from './MatchWorkspaceContext';
import { useMatchRealtime } from '@/pages/matches/matchRealtime';
import { setActiveMatchDirtyLabels } from './matchDirtyRegistry';

type RegisterDirty = (key: string, dirty: boolean, label: string) => void;
const MatchRuntimeContext = createContext<RegisterDirty | null>(null);
export const MatchRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const workspaceId = useMatchWorkspace();
  const client = useQueryClient();
  const location = useLocation();
  const inMatches = location.pathname === '/matches' || location.pathname.startsWith('/matches/');
  const [, setDirty] = useState<Record<string, string>>({});
  const realtime = useMatchRealtime(inMatches ? workspaceId : '');
  const registerDirty = useCallback((key: string, value: boolean, label: string) => {
    setDirty(current => {
      if (value && current[key] === label) return current;
      if (!value && !(key in current)) return current;
      const next = { ...current };
      if (value) next[key] = label; else delete next[key];
      setActiveMatchDirtyLabels(Array.from(new Set(Object.values(next))));
      return next;
    });
  }, []);
  useEffect(() => {
    const refresh = (event: Event) => {
      const rejectedWorkspace = (event as CustomEvent<{ workspaceId?: string }>).detail?.workspaceId;
      if (workspaceId && rejectedWorkspace === workspaceId) void client.invalidateQueries({ queryKey: ['matches-v2', workspaceId] });
    };
    window.addEventListener('matches-v2-forbidden', refresh);
    return () => window.removeEventListener('matches-v2-forbidden', refresh);
  }, [client, workspaceId]);

  useEffect(() => () => { setActiveMatchDirtyLabels([]); }, []);

  return <MatchRuntimeContext.Provider value={registerDirty}>
    {children}
    {inMatches && realtime.status === 'disconnected' && <p role="status" className="sr-only">Live match updates are disconnected. Data can still be refreshed manually.</p>}
  </MatchRuntimeContext.Provider>;
};

export const useMatchDirtyGuard = (key: string, dirty: boolean, label: string): void => {
  const registerDirty = useContext(MatchRuntimeContext);
  useEffect(() => {
    registerDirty?.(key, dirty, label);
    return () => registerDirty?.(key, false, label);
  }, [dirty, key, label, registerDirty]);
};
